<script setup lang="ts">
import { useLocationStore } from '~/stores/LocationStore'
import type { SectionLocationListRecord } from '~/types/datocms'

const props = defineProps<{
  data: SectionLocationListRecord
  moduleIndex: number
}>()

const { level, isSemantic } = useSectionRules(props.moduleIndex, !!props.data.title)

const router = useRouter()
const localeRoute = useLocaleRoute()
const route = useRoute()
const store = useLocationStore()
const appStore = useAppStore()
const { locale } = useI18n()

// Set pages per data load
store.setItemsPerPage(props.data.recordsPerPage as number)

function loadData(initial: boolean = true) {
  // Handle initial load vs. loadMore
  if (initial) {
    store.setLoading(true)
    store.resetLocations()
  } else {
    store.setLoadingMore(true)
  }

  if (route.query.category || route.query.country) {
    // Fetch Articles by Category
    const category = store.getCategoryBySlug(route.query.category as string)
    const country = store.getCountryBySlug(route.query.country as string)
    store.fetchLocations(locale.value, category?.id, country?.id)
  } else {
    // Unfiltered / All Article
    store.fetchLocations(locale.value)
  }
}

watch(
  () => route.query,
  () => {
    loadData()
  }
)

onMounted(() => {
  appStore.setTheme('dark')
  appStore.setGradientHeader('cream')

  loadData()
})

const mappedCategories = store.allCategories?.map((item) => ({
  label: item.name,
  value: item.id,
  slug: item.slug
}))

mappedCategories?.unshift({ label: '', value: undefined }, { label: 'All locations', value: '' })

const mappedCountries = store.allCountries?.map((item) => ({
  label: item.name,
  value: item.id,
  slug: item.slug
}))

mappedCountries?.unshift({ label: '', value: undefined }, { label: 'All countries', value: '' })

const selectedCategory = ref(
  mappedCategories.find((el) => el.slug === route.query.category)?.value ?? ''
)
const selectedCountry = ref(
  mappedCountries.find((el) => el.slug === route.query.country)?.value ?? ''
)

const handleFilters = () => {
  const category = store.getCategoryById(selectedCategory.value as string)
  const country = store.getCountryById(selectedCountry.value as string)

  router.push(
    localeRoute({
      path: route.path,
      query: {
        category: category ? category.slug : undefined,
        country: country ? country.slug : undefined
      }
    })
  )
}
</script>

<template>
  <div class="flex min-h-screen flex-col text-yellow">
    <div class="bg-gradient-light bg-cream pb-8 pt-30 lg:pb-20 lg:text-center">
      <div class="px-4 lg:px-8">
        <Heading
          v-if="data.title"
          :level="level"
          :size="1"
          :is-semantic="isSemantic"
          class="font-light"
          >{{ data.title }}</Heading
        >
        <div class="mt-12 flex flex-col gap-2 lg:mx-auto lg:mt-24 lg:w-fit lg:flex-row lg:gap-4">
          <FormPicker
            v-model="selectedCategory"
            label="Show"
            :options="mappedCategories"
            @update:model-value="handleFilters"
          />
          <FormPicker
            v-model="selectedCountry"
            label="In"
            :options="mappedCountries"
            @update:model-value="handleFilters"
          />
        </div>
      </div>
    </div>
    <div class="relative h-full min-h-screen flex-1 bg-cream">
      <div class="relative z-[3] px-4 lg:px-8">
        <ClientOnly>
          <LocationList
            :data="store.allLocations"
            :is-loading="store.isLoading"
            :load-more-label="data.loadMoreLabel"
            :no-records-message="'No locations'"
          />
        </ClientOnly>
      </div>
    </div>
    <Transition
      enter-from-class="opacity-0 scale-75"
      leave-to-class="opacity-0 scale-75"
      enter-active-class="transition-all duration-500"
      leave-active-class="transition-all duration-300"
    >
      <LoadMore
        v-show="store.isButtonVisible"
        :label="data.loadMoreLabel"
        :loading="store.isLoadingMore"
        invert
        @click="loadData(false)"
      />
    </Transition>
  </div>
</template>
